'use client'

import CheckboxContent from '@component/form/checkbox/content'
import MaskLoading from '@component/loading/mask'
import useControl from '@component/mapbox/control/useControl'
import { useMapboxContext } from '@component/mapbox/map/context'
import EstimateMarker from '@component/mapbox/marker/house/estimate'
import ForRentMarker from '@component/mapbox/marker/house/rent'
import ForSaleMarker from '@component/mapbox/marker/house/sale'
import SoldMarker from '@component/mapbox/marker/house/sold'
import { SearchTypeMenuContent } from '@component/mapbox/searchType'
import { HOUSE_TYPE_ID } from '@config'
import useFetch from '@hooks/useFetch'
import { buildUrl } from '@plugin/http/lib/utils'
import { combinePoints, toNumber } from '@utils'
import classNames from 'classnames'
import { assign, filter, find, map, omit, size } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useAboutAreaContext } from '../context'
import SearchTypeControlProvider, { useSearchTypeControlContext } from './context'

function TypeItem({
  className,
  value,
  onChange,
  theme,
  text,
  children,
}) {

  return (
    <div className={classNames('mt-10 first:mt-0', className)}>
      {
        children
          ? children
          : (
            <CheckboxContent dotClassName={classNames(value ? '!border-0' : '!border-neutral-3')} theme={theme} checked={value} onChange={onChange}>
              <div className='text-sm w-134'>{text}</div>
            </CheckboxContent>
          )
      }
    </div>
  )
}

function SearchTypeMenu() {
  const {
    showSale,
    showRent,
    showSold,
    showEstimate,
    toggleSale,
    toggleRent,
    toggleSold,
    toggleEstimate,
  } = useSearchTypeControlContext()

  return useControl(
    <SearchTypeMenuContent
      showSale={showSale}
      showRent={showRent}
      showSold={showSold}
      showEstimate={showEstimate}
      toggleSale={toggleSale}
      toggleRent={toggleRent}
      toggleSold={toggleSold}
      toggleEstimate={toggleEstimate}
      saleText='For Sale'
      rentText='For Rent'
      soldText='Recently sold'
      estimateText='Estimated values'
      TypeItem={TypeItem}
    />,
    { position: 'top-right', groupClass: false, className: '!transform-none' }
  )
}

export default function SearchType({
  data
}) {

  return (
    <SearchTypeControlProvider data={data}>
      <SearchTypeMenu />
      <SearchTypeMarkers />
    </SearchTypeControlProvider>
  )
}

export function SearchTypeMarkers() {
  const {
    showSale,
    showRent,
    showSold,
    showEstimate,
  } = useSearchTypeControlContext()
  const { commonData } = useAboutAreaContext()
  const showData = !commonData.currentMenu

  const { map: mapInstance, mapLoaded } = useMapboxContext()

  const [bounds, setBounds] = useState()
  const [rect, setRect] = useState()

  useEffect(() => {
    if (!mapLoaded) return

    const handleChange = () => {
      const [rect, bounds] = getBounds(mapInstance)
      setBounds(bounds)
      setRect(rect)
    }
    handleChange()

    mapInstance.on('dragend', handleChange)
    mapInstance.on('zoomend', handleChange)

    return () => {
      mapInstance.off('dragend', handleChange)
      mapInstance.off('zoomend', handleChange)
    }

  }, [mapInstance, mapLoaded, setBounds, setRect])

  const [saleData, saleLoading] = useTypeData({ bounds, rect, typeId: HOUSE_TYPE_ID.residential, visible: showSale && showData })
  const [rentData, rentLoading] = useTypeData({ bounds, rect, typeId: HOUSE_TYPE_ID.rent, visible: showRent && showData })
  const [soldData, soldLoading] = useTypeData({ bounds, rect, typeId: HOUSE_TYPE_ID.sold, visible: showSold && showData })
  const [propertyData, propertyLoading] = usePropertyData({ bounds, rect, visible: showEstimate && showData })

  // estimate数据需要和forsale数据合并
  // estimate数据如果在forsale中出现，则不显示这个estimate数据
  const combinePropertyData = useMemo(() => {
    if (!size(saleData)) return propertyData
    return filter(propertyData, item => {
      const lat = toNumber(item.lat)
      const lng = toNumber(item.lng)
      const houseId = toNumber(item.houseId)
      return !find(saleData, jtem => (toNumber(jtem.id) === houseId || (toNumber(jtem.lat) === lat && toNumber(jtem.lng) === lng)))
    })
  }, [saleData, propertyData])

  // sold数据需要和forsale数据合并
  // sold数据如果在forsale中出现，则不显示这个sold数据
  const combineSoldData = useMemo(() => {
    if (!size(saleData)) return soldData
    return filter(soldData, item => {
      const lat = toNumber(item.lat)
      const lng = toNumber(item.lng)
      return !find(saleData, jtem => (toNumber(jtem.lat) === lat && toNumber(jtem.lng) === lng))
    })
  }, [saleData, soldData])

  const salePoints = combinePoints(saleData)
  const rentPoints = combinePoints(rentData)
  const soldPoints = combinePoints(combineSoldData)
  const propertyPoints = combinePoints(combinePropertyData)
  const isLoading = saleLoading || rentLoading || soldLoading || propertyLoading

  return (
    <>
      {
        map(salePoints, item => (
          item.lng && item.lat && (
            <ForSaleMarker
              data={item}
              key={item.id + item.slug}
            ></ForSaleMarker>
          )
        ))
      }
      {
        map(rentPoints, item => (
          item.lng && item.lat && (
            <ForRentMarker
              data={item}
              key={item.id + item.slug}
            ></ForRentMarker>
          )
        ))
      }
      {
        map(soldPoints, item => (
          item.lng && item.lat && (
            <SoldMarker
              data={item}
              key={item.id + item.slug}
            ></SoldMarker>
          )
        ))
      }
      {
        map(propertyPoints, item => (
          item.lng && item.lat && (
            <EstimateMarker
              data={item}
              key={item.id + item.slug}
            ></EstimateMarker>
          )
        ))
      }
      <MaskLoading transparent loading={isLoading}></MaskLoading>
    </>
  )
}

function useTypeData({
  bounds,
  rect,
  typeId,
  visible,
}) {

  const [mapData, setMapData] = useState([])
  const params = useMemo(() => {
    if (!visible || !rect) return

    const rIds = map(filter(mapData, item => bounds.contains([item.lng, item.lat])), item => item.id)

    return assign(
      { rect, getPoint: 1 },
      size(rIds) ? { rIds } : undefined,
      typeId ? { typeId } : undefined,
    )
  }, [bounds, rect, mapData, typeId, visible])

  const { data, isLoading } = useFetch(
    visible && params
      ? { url: 'houses/list', params, fetchKey: buildUrl('houses/list', undefined, omit(params, 'rIds')) }
      : null,
    {
      keepPreviousData: true
    }
  )
  useEffect(() => setMapData(data), [data, setMapData])

  return [
    visible ? data : [],
    isLoading
  ]
}

function usePropertyData({
  bounds,
  rect,
  visible
}) {
  const [mapData, setMapData] = useState([])
  const params = useMemo(() => {
    if (!visible) return

    const rIds = map(filter(mapData, item => bounds.contains([item.lng, item.lat])), item => item.id)
    const mapTypeIds = [HOUSE_TYPE_ID.estimate, HOUSE_TYPE_ID.rv].join(',')

    return assign(
      { rect },
      size(rIds) ? { rIds } : undefined,
      mapTypeIds ? { mapTypeIds } : undefined
    )
  }, [bounds, rect, mapData, visible])
  const { data, isLoading } = useFetch(
    visible && params
      ? { url: 'property-map', params, fetchKey: buildUrl('property-map', undefined, omit(params, 'rIds')) }
      : null,
    {
      keepPreviousData: true
    }
  )
  useEffect(() => setMapData(data), [data, setMapData])

  return [
    visible ? data : [],
    isLoading
  ]
}

function getBounds(map) {
  let bounds = map.getBounds()
  let ne = bounds.getNorthEast()
  let sw = bounds.getSouthWest()
  return [
    [sw.lng, ne.lng < 0 ? 180 : ne.lng, sw.lat, ne.lat],
    bounds
  ]
}